import React, { useCallback, useEffect, useMemo, useState } from "react";
import GlobalDirectoryPaginationSection from "../../../../global/directory/GlobalDirectoryPaginationSection";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import VisitorDirectoryFilters from "./VisitorDirectoryFilters";
import VisitorTable from "./VisitorTable";
import ViewVisitorModal from "../modals/ViewVisitorModal";
import { getVisitorColumnDefs } from "../../utils/visitorColumnDefs";
import { VISITOR_PAGE_SIZE } from "../../utils/visitorListConstants";
import {
  applyVisitorDirectoryFilters,
  buildVisitorExportRows,
  hasActiveVisitorDirectoryFilters,
  INITIAL_VISITOR_DIRECTORY_FILTERS,
} from "../../utils/visitorDirectoryFilters";
import { useVisitorList } from "../../utils/useVisitor";
import { VISITOR_DATA_CHANGED_EVENT } from "../../utils/visitorDataEvents";

export default function VisitorDirectorySection({
  isAdmin = false,
  listScope = "self",
  showPanelHead = false,
  permissions,
  roleSlug,
  onEdit,
  onDelete,
  onExit,
  onSlip,
  reloadToken = 0,
  emptyBaseline = "No visitor records yet.",
  emptyFiltered = "No records available",
}) {
  const { allRows, loading, refresh } = useVisitorList({ listScope });

  useEffect(() => {
    if (reloadToken) refresh();
  }, [reloadToken, refresh]);

  useEffect(() => {
    const onDataChanged = () => refresh();
    window.addEventListener(VISITOR_DATA_CHANGED_EVENT, onDataChanged);
    return () => window.removeEventListener(VISITOR_DATA_CHANGED_EVENT, onDataChanged);
  }, [refresh]);

  const [filters, setFilters] = useState(() => ({ ...INITIAL_VISITOR_DIRECTORY_FILTERS }));
  const [columnVisibility, setColumnVisibility] = useState(null);
  const [exportError, setExportError] = useState("");
  const [viewVisitorId, setViewVisitorId] = useState(null);

  const filtered = useMemo(
    () => applyVisitorDirectoryFilters(allRows, filters),
    [allRows, filters],
  );

  const pageSize = VISITOR_PAGE_SIZE;
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(filters.page || 1, totalPages);

  const rows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const columnDefs = getVisitorColumnDefs(isAdmin);
  const visibleColumns = useMemo(() => {
    if (!columnVisibility) return columnDefs;
    return columnDefs.filter((col) => {
      if (col.id === "actions") return true;
      return columnVisibility[col.id] !== false;
    });
  }, [columnDefs, columnVisibility]);

  const handleExportCsv = useCallback(() => {
    setExportError("");
    try {
      const exportRows = buildVisitorExportRows(filtered, visibleColumns);
      if (!exportRows.length) {
        setExportError("No visitors found for export");
        return;
      }
      const headers = Object.keys(exportRows[0]);
      const csv = [
        headers.join(","),
        ...exportRows.map((record) =>
          headers
            .map((key) => {
              const value = String(record[key] ?? "").replace(/"/g, '""');
              return `"${value}"`;
            })
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      link.download = `visitors-${y}-${m}-${d}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.message || "Failed to export CSV");
    }
  }, [filtered, visibleColumns]);

  const hasFilters = hasActiveVisitorDirectoryFilters(filters);
  const emptyMessage =
    allRows.length === 0 && !hasFilters ? emptyBaseline : emptyFiltered;

  const directoryHint = isAdmin
    ? "All office and reception entries. Registered By shows which staff member added each visitor."
    : "Only visitors you registered while logged in are listed here.";

  return (
    <>
      {showPanelHead ? (
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Visitor directory</h3>
          <p className="user-builder-panel__hint">{directoryHint}</p>
        </div>
      ) : null}
      <GlobalFeedback type="error" text={exportError} role="alert" />
      <VisitorDirectoryFilters
        isAdmin={isAdmin}
        filters={filters}
        onFilterChange={setFilters}
        rows={rows}
        catalogRows={allRows}
        onColumnVisibilityChange={setColumnVisibility}
        onExportCsv={permissions?.canExport ? handleExportCsv : undefined}
      />
      <VisitorTable
        rows={rows}
        loading={loading}
        isAdmin={isAdmin}
        permissions={permissions}
        roleSlug={roleSlug}
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(v) => setViewVisitorId(v.id)}
        onEdit={onEdit}
        onDelete={onDelete}
        onExit={onExit}
        onSlip={onSlip}
        emptyMessage={emptyMessage}
        columnVisibilityOverride={columnVisibility}
        showPaginationFooter={false}
      />
      <GlobalDirectoryPaginationSection
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        label="Visitor directory pagination"
      />

      {viewVisitorId ? (
        <ViewVisitorModal
          visitorId={viewVisitorId}
          isAdmin={isAdmin}
          onClose={() => setViewVisitorId(null)}
        />
      ) : null}
    </>
  );
}
